import React, { FC } from 'react';

import { getIn, useFormikContext } from 'formik';

import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Switch } from 'components/ui/switch';

import { GeneralThemeFormValues } from '../../GeneralSettings/generalTheme.types';

import { FormField } from './FormField';

type FooterSectionFormValues = NonNullable<GeneralThemeFormValues['footer']> & {
  supportParams?: {
    playerIdParamName?: string;
    playerNameParamName?: string;
    playerEmailParamName?: string;
  };
};

type FooterUrlFieldKey =
  | 'appstoreLink'
  | 'supportLink'
  | 'googlePlayLink'
  | 'privacyPolicyUrl'
  | 'termsAndConditionsUrl';

export const FooterSectionFields: FC = () => {
  const {
    values: footer,
    handleBlur,
    handleChange,
    setFieldValue,
    errors,
    touched
  } = useFormikContext<FooterSectionFormValues>();

  const footerUrlFieldMeta = (fieldKey: FooterUrlFieldKey) => {
    const t = touched[fieldKey];
    const e = errors[fieldKey];
    const hasError = Boolean(t && e);
    return {
      error: hasError,
      helperText: hasError && e !== undefined ? String(e) : undefined
    };
  };

  const footerSupportParamFieldMeta = (
    path:
      | 'supportParams.playerIdParamName'
      | 'supportParams.playerNameParamName'
  ) => {
    const t = getIn(touched, path);
    const e = getIn(errors, path);
    const hasError = Boolean(t && e);
    return {
      error: hasError,
      helperText: hasError && e !== undefined ? String(e) : undefined
    };
  };

  return (
    <>
      <Label className="mb-6 block font-sans text-base font-semibold leading-5 text-base-secondaryForeground md:col-span-2">
        External links
      </Label>
      <div className="mb-6 flex flex-row flex-wrap items-center justify-start gap-9 md:col-span-2 md:flex-nowrap md:items-start">
        <RadioGroup
          value={footer.openLinksInNewTab ? 'New tab' : 'Modal'}
          onValueChange={(v) =>
            setFieldValue('openLinksInNewTab', v === 'New tab')
          }
          className="flex flex-row gap-9"
        >
          <div className="flex items-start gap-2">
            <RadioGroupItem
              value="New tab"
              id="footer-new-tab"
              className="mt-0.5 shrink-0 border-base-border text-indigo-600 shadow-none focus-visible:ring-indigo-600"
            />
            <div className="flex flex-col gap-0.5">
              <Label
                htmlFor="footer-new-tab"
                className="cursor-pointer font-sans text-sm font-medium text-base-foreground"
              >
                New Tab
              </Label>
              <span className="font-sans text-xs font-normal leading-4 text-base-mutedForeground">
                Opens the link in a new browser tab.
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <RadioGroupItem
              value="Modal"
              id="footer-modal"
              className="mt-0.5 shrink-0 border-base-border text-indigo-600 shadow-none focus-visible:ring-indigo-600"
            />
            <div className="flex flex-col gap-0.5">
              <Label
                htmlFor="footer-modal"
                className="cursor-pointer font-sans text-sm font-medium text-base-foreground"
              >
                Modal
              </Label>
              <span className="font-sans text-xs font-normal leading-4 text-base-mutedForeground">
                Opens the link in an in-app modal window.
              </span>
            </div>
          </div>
        </RadioGroup>
        <div className="flex shrink-0 items-start gap-2">
          <Switch
            id="footer-cookie-settings"
            checked={!!footer.showCookieSettings}
            onCheckedChange={(checked) =>
              setFieldValue('showCookieSettings', checked)
            }
            data-testid="footerShowCookieSettings"
            className="mt-0.5 h-5 w-9 shrink-0 shadow-none bg-zinc-200 data-[state=unchecked]:bg-zinc-200 data-[state=checked]:bg-base-primary [&>span]:bg-base-background data-[state=checked]:[&>span]:translate-x-4"
          />
          <div className="space-y-0.5">
            <Label
              htmlFor="footer-cookie-settings"
              className="cursor-pointer font-sans text-sm font-medium text-base-foreground"
            >
              Show &quot;Cookie Settings&quot; Link
            </Label>
            <p className="font-sans text-xs font-normal leading-4 text-base-mutedForeground">
              Controls whether the &quot;Cookie Settings&quot; link is displayed.
            </p>
          </div>
        </div>
      </div>
      <div className="mt-1 grid grid-cols-1 gap-y-9 md:grid-cols-2 md:gap-x-9 md:col-span-2 [&_input:not([aria-invalid])]:border-zinc-200 [&_input]:placeholder:text-base-mutedForeground">
        <FormField
          label="Appstore Link"
          name="appstoreLink"
          value={footer.appstoreLink}
          onChange={handleChange}
          onBlur={handleBlur}
          {...footerUrlFieldMeta('appstoreLink')}
          placeholder="https://"
          type="url"
          dataTestId="footerAppstoreLink"
        />
        <FormField
          label="Support Link"
          name="supportLink"
          value={footer.supportLink}
          onChange={handleChange}
          onBlur={handleBlur}
          {...footerUrlFieldMeta('supportLink')}
          placeholder="https://"
          type="url"
          dataTestId="footerSupportLink"
        />
        <FormField
          label="GooglePlay Link"
          name="googlePlayLink"
          value={footer.googlePlayLink}
          onChange={handleChange}
          onBlur={handleBlur}
          {...footerUrlFieldMeta('googlePlayLink')}
          placeholder="https://"
          type="url"
          dataTestId="footerGooglePlayLink"
        />
        <FormField
          label="Player ID Parameter Name"
          name="supportParams.playerIdParamName"
          value={footer.supportParams?.playerIdParamName ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          {...footerSupportParamFieldMeta('supportParams.playerIdParamName')}
          placeholder="e.g., playerId, uid, customer_id"
          dataTestId="footerPlayerIdParamName"
        />
        <FormField
          label="Privacy Policy"
          name="privacyPolicyUrl"
          value={footer.privacyPolicyUrl}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          {...footerUrlFieldMeta('privacyPolicyUrl')}
          placeholder="https://"
          type="url"
          dataTestId="footerPrivacyPolicyUrl"
        />
        <FormField
          label="Player Name Parameter Name"
          name="supportParams.playerNameParamName"
          value={footer.supportParams?.playerNameParamName ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          {...footerSupportParamFieldMeta('supportParams.playerNameParamName')}
          placeholder="e.g., name, user_name, customer_name"
          dataTestId="footerPlayerNameParamName"
        />
        <FormField
          label="Terms and Conditions"
          name="termsAndConditionsUrl"
          value={footer.termsAndConditionsUrl}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          {...footerUrlFieldMeta('termsAndConditionsUrl')}
          placeholder="https://"
          type="url"
          dataTestId="footerTermsAndConditionsUrl"
        />
      </div>
    </>
  );
};
